import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../api';
import './OrgTiers.scss';

interface TierSignals {
  maxWarmth: number;
  bestConversationStatus: string | null;
  replyCategories: string[];
  totalEmailsSent: number;
  hasPositiveReply: boolean;
  prospectCount: number;
  calculatedAt: string;
}

interface OrgTierRow {
  id: number;
  knownOrgId: number;
  oagentId: number;
  tier: string;
  tierUpdatedAt: string;
  tierSignals: TierSignals;
  orgName: string | null;
  orgDomain: string | null;
  campaignName: string | null;
  prospectCount: number;
}

const TIER_COLORS: Record<string, string> = {
  opportunity: '#10b981', // green
  A: '#ef4444', // red (hot)
  B: '#f97316', // orange (warm)
  C: '#3b82f6', // blue (cold)
  D: '#6b7280', // gray (dead)
  unknown: '#9ca3af', // light gray
};

const TIER_LABELS: Record<string, string> = {
  opportunity: '🎯 Opportunity',
  A: '🔥 Tier A (Hot)',
  B: '🌡️ Tier B (Warm)',
  C: '❄️ Tier C (Cold)',
  D: '💀 Tier D (Dead)',
  unknown: '❓ Unknown',
};

const OrgTiers: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [tiers, setTiers] = useState<OrgTierRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterTier, setFilterTier] = useState<string>('');
  const [recalculating, setRecalculating] = useState(false);

  const fetchTiers = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams();
      if (filterTier) params.append('tier', filterTier);
      params.append('limit', '500');

      const response = await api.get(`/org-tiers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setTiers(response.data.tiers || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tiers');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      const token = await getAccessTokenSilently();
      await api.post(
        '/org-tiers/recalculate',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Refetch after a short delay
      setTimeout(fetchTiers, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to recalculate');
    } finally {
      setRecalculating(false);
    }
  };

  useEffect(() => {
    fetchTiers();
  }, [filterTier]);

  // Group tiers by tier type
  const tierGroups = tiers.reduce(
    (acc, row) => {
      const tier = row.tier;
      if (!acc[tier]) acc[tier] = [];
      acc[tier].push(row);
      return acc;
    },
    {} as Record<string, OrgTierRow[]>,
  );

  const tierOrder = ['opportunity', 'A', 'B', 'C', 'D', 'unknown'];

  if (loading) {
    return (
      <div className='org-tiers-page'>
        <div className='loading'>Loading tiers...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='org-tiers-page'>
        <div className='error'>Error: {error}</div>
      </div>
    );
  }

  return (
    <div className='org-tiers-page inset-white-backed'>
      <div className='org-tiers-header'>
        <h1>🎯 Org Campaign Tiers</h1>
        <div className='controls'>
          <select
            value={filterTier}
            onChange={(e) => setFilterTier(e.target.value)}
          >
            <option value=''>All Tiers</option>
            {tierOrder.map((tier) => (
              <option key={tier} value={tier}>
                {TIER_LABELS[tier]}
              </option>
            ))}
          </select>
          <button
            onClick={handleRecalculate}
            disabled={recalculating}
            className='recalc-btn'
          >
            {recalculating ? 'Recalculating...' : '🔄 Recalculate'}
          </button>
          <button onClick={fetchTiers} className='refresh-btn'>
            ↻ Refresh
          </button>
        </div>
      </div>

      <div className='tier-summary'>
        {tierOrder.map((tier) => {
          const count = tierGroups[tier]?.length || 0;
          return (
            <div
              key={tier}
              className='tier-badge'
              style={{ backgroundColor: TIER_COLORS[tier] }}
              onClick={() => setFilterTier(tier === filterTier ? '' : tier)}
            >
              <span className='tier-name'>{tier.toUpperCase()}</span>
              <span className='tier-count'>{count}</span>
            </div>
          );
        })}
      </div>

      <div className='tier-columns'>
        {tierOrder
          .filter((tier) => !filterTier || tier === filterTier)
          .map((tier) => {
            const rows = tierGroups[tier] || [];
            return (
              <div key={tier} className='tier-column'>
                <div
                  className='tier-column-header'
                  style={{ backgroundColor: TIER_COLORS[tier] }}
                >
                  {TIER_LABELS[tier]} ({rows.length})
                </div>
                <div className='tier-column-content'>
                  {rows.map((row) => (
                    <div key={row.id} className='org-card'>
                      <div className='org-name'>
                        {row.orgName || 'Unknown Org'}
                      </div>
                      <div className='org-domain'>{row.orgDomain || '-'}</div>
                      <div className='org-campaign'>{row.campaignName}</div>
                      <div className='org-signals'>
                        <span title='Warmth'>
                          🌡️ {row.tierSignals.maxWarmth}
                        </span>
                        <span title='Emails Sent'>
                          ✉️ {row.tierSignals.totalEmailsSent}
                        </span>
                        <span title='Prospects'>
                          👤 {row.tierSignals.prospectCount}
                        </span>
                      </div>
                      <div className='org-status'>
                        {row.tierSignals.bestConversationStatus || 'No status'}
                      </div>
                      {row.tierSignals.replyCategories.length > 0 && (
                        <div className='org-categories'>
                          {row.tierSignals.replyCategories.join(', ')}
                        </div>
                      )}
                      <div className='org-updated'>
                        {new Date(row.tierUpdatedAt).toLocaleString()}
                      </div>
                    </div>
                  ))}
                  {rows.length === 0 && (
                    <div className='empty-column'>No orgs in this tier</div>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default OrgTiers;
